import { useEffect, useState } from "react";

/*
 * Resolve a CSS custom property to a concrete color string.
 * Recharts applies stroke/fill/tick colors as SVG presentation attributes,
 * where CSS var() does not resolve, so chart code must pass real values.
 */
export function cssVar(name, fallback = "") {
  if (typeof window === "undefined" || !document?.documentElement) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Chart colors resolved from the active theme. */
export function chartColors() {
  return {
    brand: cssVar("--brand", "#8b93ff"),
    signal: cssVar("--signal", "#2fd4e7"),
    ok: cssVar("--ok", "#34d399"),
    critical: cssVar("--sev-critical", "#ff5c7a"),
    high: cssVar("--sev-high", "#ff9157"),
    medium: cssVar("--sev-medium", "#f5c451"),
    low: cssVar("--sev-low", "#4ba9f7"),
    info: cssVar("--sev-info", "#8f9cb0"),
    grid: cssVar("--line", "#1b2433"),
    axis: cssVar("--text-3", "#7b8aa0"),
    text: cssVar("--text", "#eaf0f7"),
    panel: cssVar("--panel", "#0d1219"),
    cat: Array.from({ length: 9 }, (_, i) => cssVar(`--cat-${i}`, "#8f9cb0")),
  };
}

export const resolveVar = (v) => {
  const m = /^var\((--[\w-]+)\)$/.exec(String(v || ""));
  return m ? cssVar(m[1], "#8f9cb0") : v;
};

/** Re-render when the theme attribute changes, so charts pick up new colors. */
export function useThemeVersion() {
  const [v, setV] = useState(0);
  useEffect(() => {
    const obs = new MutationObserver(() => setV((n) => n + 1));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return v;
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}
